import React from 'react';
import {
  Button,
  CircularProgress,
  Box,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  Checkbox
} from '@material-ui/core';
import { useCustomReportButton } from './useCustomReport';

const useStyles = makeStyles(theme => ({
  section: {
    width: '100%',
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#fff'
  },
  content: {
    padding: theme.spacing(2)
  },
  tableHeader: {
    backgroundColor: '#f57c00'
  },
  tableHeaderCell: {
    color: 'white',
    fontWeight: 'bold'
  }
}));

const CustomReportTable = () => {
  const classes = useStyles();
  const {
    loading,
    reportData,
    filteredReportData,
    reportDefinition,
    page,
    rowsPerPage,
    buttonLabel,
    disableActionButton,
    buttonVariant,
    buttonColor,
    handleClick,
    handleChangePage,
    handleChangeRowsPerPage,
    selectedItems,
    toggleItemSelection,
    clearSelection,
    handleAssignItems,
    handleUnassignItems,
    assigning,
    allow_unassign,
    isViewMode
  } = useCustomReportButton();

  const displayData = isViewMode ? filteredReportData : reportData;

  const handleSelectAll = event => {
    if (event.target.checked) {
      const allIds = displayData.map(row => row.sub_id);
      allIds.forEach(id => toggleItemSelection(id));
    } else {
      clearSelection();
    }
  };

  const isAllSelected = displayData.length > 0 && selectedItems.length === displayData.length;
  const isIndeterminate = selectedItems.length > 0 && selectedItems.length < displayData.length;

  return (
    <Box className={classes.section}>
      <Box className={classes.content}>
        {loading && !reportDefinition ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : reportDefinition ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow className={classes.tableHeader}>
                  {!isViewMode && (
                    <TableCell className={classes.tableHeaderCell} style={{ width: 50 }}>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        disabled={loading || assigning}
                      />
                    </TableCell>
                  )}
                  {reportDefinition.jsonSettings?.headers &&
                    Object.entries(reportDefinition.jsonSettings.headers).map(
                      ([key, caption], index) => (
                        <TableCell
                          key={index}
                          className={classes.tableHeaderCell}
                        >
                          {caption}
                        </TableCell>
                      )
                    )}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(displayData) &&
                  displayData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow key={row.sub_id || index}>
                        {!isViewMode && (
                          <TableCell>
                            <Checkbox
                              checked={selectedItems.includes(row.sub_id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleItemSelection(row.sub_id);
                              }}
                              disabled={loading || assigning}
                            />
                          </TableCell>
                        )}
                        {reportDefinition.jsonSettings?.headers &&
                          Object.keys(
                            reportDefinition.jsonSettings.headers
                          ).map((key, colIndex) => (
                            <TableCell key={colIndex}>{row[key]}</TableCell>
                          ))}
                      </TableRow>
                    ))}
                {(!Array.isArray(displayData) || displayData.length === 0) && (
                  <TableRow>
                    <TableCell
                      colSpan={
                        reportDefinition.jsonSettings?.headers
                          ? Object.keys(reportDefinition.jsonSettings.headers)
                              .length + (isViewMode ? 0 : 1)
                          : isViewMode ? 1 : 2
                      }
                      align="center"
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" gap={2}>
                {!isViewMode && (
                  <Button
                    onClick={handleClick}
                    disabled={disableActionButton}
                    variant={buttonVariant}
                    color={buttonColor}
                    size="small"
                  >
                    {buttonLabel}
                  </Button>
                )}
                {!isViewMode && selectedItems.length > 0 && (
                  <>
                    <Button
                      onClick={allow_unassign ? handleUnassignItems : handleAssignItems}
                      disabled={assigning}
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ display: 'none' }}
                      id="hidden-assign-unassign-button"
                    >
                      {assigning
                        ? (allow_unassign ? 'Unassigning...' : 'Assigning...')
                        : `${allow_unassign ? 'Unassign' : 'Assign'} Selected (${selectedItems.length})`}
                    </Button>
                    <Button
                      onClick={clearSelection}
                      variant="outlined"
                      size="small"
                    >
                      Clear Selection ({selectedItems.length})
                    </Button>
                  </>
                )}
              </Box>
              <Box>
                <TablePagination
                  component="div"
                  count={Array.isArray(displayData) ? displayData.length : 0}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                />
              </Box>
            </Box>
          </TableContainer>
        ) : (
          <Box p={2}>
            {disableActionButton
              ? 'No custom report data available'
              : 'Click the button to load the custom report'}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomReportTable;